import { computed, effect, inject, Injectable, Renderer2, RendererFactory2, signal } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { VoltaAuthService } from '@core/auth/volta-auth.service';
import { VoltaCommon } from '@core/common/volta-common';
import { filter, map } from 'rxjs';
import { FooterToolbarService } from './components/footer-toolbar/footer-toolbar.service';
import { NavBarState, UIMode, UITheme } from './ui.model';

@Injectable({
    providedIn: 'root',
})
export class UIService {
    private router = inject(Router);
    private authService = inject(VoltaAuthService);
    private rendererFactory = inject(RendererFactory2);
    private footerToolbarService = inject(FooterToolbarService);

    private _mode = signal<UIMode>(UIMode.Light);
    private _theme = signal<UITheme>(UITheme.Orange);
    private _navBarState = signal<NavBarState>(NavBarState.Expanded);
    private _navBarIndex = signal<number|undefined>(undefined);
    private _navBarOpen = signal<boolean>(false);
    private _changing = signal<boolean>(false);
    private _navigationBar = signal<boolean>(true);
    private _statusBar = signal<boolean>(true);
    private _companyBar = signal<boolean>(false);
    private _footerToolbar = signal<boolean>(false);
    private _trademark = signal<boolean>(false);
    private _loadingScreen = signal<boolean>(false);
    private _windowSize = signal<number>(window.innerWidth);

    mode = this._mode.asReadonly();
    theme = this._theme.asReadonly();
    navBarState = this._navBarState.asReadonly();
    navBarIndex = this._navBarIndex.asReadonly();
    navBarOpen = this._navBarOpen.asReadonly();
    changing = this._changing.asReadonly();
    navigationBar = this._navigationBar.asReadonly();
    statusBar = this._statusBar.asReadonly();
    companyBar = this._companyBar.asReadonly();
    footerToolbar = this._footerToolbar.asReadonly();
    trademark = this._trademark.asReadonly();
    loadingScreen = this._loadingScreen.asReadonly();

    isMobile = computed(() => this._windowSize() < 767);
    isNavBarExpanded = computed(() => this.navBarState() == NavBarState.Expanded && !this.isMobile());

    private changeTimeout: any | undefined;
    private renderer: Renderer2;

    constructor() {
        //You cant inject Renderer2 in Service (for some reason idk...), so it is created in constructor instead.
        this.renderer = this.rendererFactory.createRenderer(null, null);

        //Get user Operating System preference
        if (window.matchMedia('(prefers-color-scheme: dark)')) {
            this._mode.set(UIMode.Dark);
        }

        this.getTheme();
        this.setTheme();
        this.getNavBarState();
        this.subscribeToUIVisibility();
        this.subscribeToLoadingScreen();
        this.subscribeToFooterToolbar();

        effect(() => {
            if(!this.navBarOpen()){
                this.setNavBarIndex(undefined);
            }
        })
    }

    setNavBarOpen(isOpen:boolean){
        this._navBarOpen.set(isOpen);
    }

    setNavBarIndex(index:number|undefined){
        this._navBarIndex.set(index);
    }

    getTheme() {
        const mode = localStorage.getItem(VoltaCommon.LOCAL_MODE);

        if (mode) {
            this._mode.set(<UIMode>mode);
        } 
        else {
            localStorage.setItem(VoltaCommon.LOCAL_MODE, this._mode());
        }
    }

    setTheme() {
        //Set Themes and Modes on Body Tag
        effect(() => {
            this.renderer.setAttribute(document.body, 'data-theme-mode', this.mode());
            this.renderer.setAttribute(document.body, 'data-theme', this.theme());

            if (this.changing()) {
                this.renderer.addClass(document.body, 'theme-transition');
            } 
            else {
                this.renderer.removeClass(document.body, 'theme-transition');
            }
        });
    }

    changeTheme() {
        this._changing.set(true);
        clearTimeout(this.changeTimeout);
        this.changeTimeout = setTimeout(() => this._changing.set(false), 1000);

        const mode = localStorage.getItem(VoltaCommon.LOCAL_MODE);

        localStorage.setItem(VoltaCommon.LOCAL_MODE, mode == UIMode.Light ? UIMode.Dark : UIMode.Light);
        this.getTheme();
    }

    getNavBarState() {
        const state = localStorage.getItem(VoltaCommon.LOCAL_NAVBAR_STATE);

        if (state != null) {
            this._navBarState.set(<NavBarState>state);
        } 
        else {
            localStorage.setItem(VoltaCommon.LOCAL_NAVBAR_STATE, this._navBarState());
        }
    }

    toggleNavBarState() {
        const state = localStorage.getItem(VoltaCommon.LOCAL_NAVBAR_STATE);

        localStorage.setItem(VoltaCommon.LOCAL_NAVBAR_STATE, state == NavBarState.Collapsed ? NavBarState.Expanded : NavBarState.Collapsed);
        this.getNavBarState();
    }

    subscribeToUIVisibility() {
        // Subscribe to UI Visibility per Route
        // Listen for NavigationEnd event to get the active route data after each navigation
        this.router.events
        .pipe(
            filter((event) => event instanceof NavigationEnd),
            map(() => {
                // Traverse through nested routes to get the last active child route with data
                // Has to be last child because UI Visibility data can be declared in different heirarchy
                let activeRoute = this.router.routerState.root;

                while (activeRoute.firstChild) {
                    activeRoute = activeRoute.firstChild;
                }
                
                return activeRoute.snapshot.data;
            })
        )
        .subscribe((data) => {
            this._navigationBar.set((data['navigationBar'] ?? true) && this.authService.isAuthorised());
            this._companyBar.set((data['companyBar'] ?? true) && this.authService.isAuthorised());
            this._statusBar.set(data['statusBar'] ?? true);
            this._trademark.set(data['trademark'] ?? false);
        });
    }

    subscribeToFooterToolbar() {
        this.footerToolbarService.visible$.subscribe({
            next: (visible) => {
                this._footerToolbar.set(visible);
            }
        })
    }

    subscribeToLoadingScreen() {
        this.router.events
        .pipe(filter((event) => event instanceof NavigationStart))
        .subscribe((data) => {
            // Show Loading Screen
            this._loadingScreen.set(true);
        });

        this.router.events
        .pipe(
            filter((event) => event instanceof NavigationEnd || event instanceof NavigationCancel)
        )
        .subscribe((data) => {
            // Hide Loading Screen
            this._loadingScreen.set(false);
        });
    }

    updateWindowSize(){
        this._windowSize.set(window.innerWidth);
    }
}
